import { Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  productGroup: ProductGroups;

  allMessagesHaveType: boolean;
  allConditionsHaveType: boolean;

  inMailsAreAllowed(): boolean;

  emailsAreAllowed(): boolean;
}

/**
 * Compact canvas badge: shows the product and – if needed – what is missing in the flow.
 * Campaign name and save state live in the builder header.
 */
const CampaignStatusPanel: React.FC<Props> = ({ productGroup, allMessagesHaveType, allConditionsHaveType, inMailsAreAllowed, emailsAreAllowed }) => {
  const { classes } = useStyles({ productGroup });
  const { t } = useTranslation();

  const flowHasError = !allMessagesHaveType || !allConditionsHaveType || !inMailsAreAllowed() || !emailsAreAllowed();

  const getHoverText = () => {
    let hoverText = '';

    if (!allMessagesHaveType) {
      hoverText += t('warningMissingMessageType') + '\n';
    }
    if (!allConditionsHaveType) {
      hoverText += t('warningMissingConditionType') + '\n';
    }
    if (!inMailsAreAllowed()) {
      hoverText += t('inMailsNotAllowed') + '\n';
    }
    if (!emailsAreAllowed()) {
      hoverText += t('emailsNotAllowed');
    }

    return hoverText.trim();
  };

  return (
    <Stack className={classes.campaignStatusPanel} direction={'row'} alignItems={'center'} gap={1}>
      <div className={classes.product}>
        {productGroup === ProductGroups.HR_BOOSTER
          ? 'Recruiting Booster'
          : productGroup === ProductGroups.LEAD_BOOSTER
          ? 'Lead Booster'
          : 'SAIA Booster'}
      </div>
      {flowHasError ? (
        <Stack className={classes.warningTextContainer} direction={'row'} alignItems={'center'} gap={0.5}>
          <Typography className={classes.warningText}>{t('whatIsWrong')}</Typography>
          <PaiHelpIcon tooltipText={getHoverText()} variant={'help'} />
        </Stack>
      ) : null}
    </Stack>
  );
};

const useStyles = makeStyles<{ productGroup: ProductGroups }>()((theme, { productGroup }) => ({
  campaignStatusPanel: {
    position: 'relative',
    zIndex: 9,
  },
  product: {
    display: 'flex',
    alignItems: 'center',
    height: '34px',
    padding: '0 12px',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    background: '#FFF',
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    color:
      productGroup === ProductGroups.HR_BOOSTER
        ? theme.palette.recruiting.main
        : productGroup === ProductGroups.LEAD_BOOSTER
        ? theme.palette.lead.main
        : theme.palette.primary.main,
  },
  warningTextContainer: {
    height: '34px',
    backgroundColor: '#FFF',
    padding: '0 8px 0 12px',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.greys.lighter}`,
  },
  warningText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    color: theme.palette.warning.main,
  },
}));

export default CampaignStatusPanel;
